import {
  CellContext,
  createColumnHelper,
  ColumnDef,
  SortingState,
} from '@tanstack/react-table'

import dayjs from 'dayjs'
import { useMemo } from 'react'

import {
  formatInteger,
  formatTwoDecimal,
  formatUsd,
  formatUsdSmartDecimal,
} from '../../../utils/formatting'
import { TruncatedLinkCell } from '../CommonTableCells/TruncatedLinkCell'
import { InitialsAvatar } from '../CommonTableCells/InitialsAvatar'
import { TruncatedTextCell } from '../CommonTableCells/TruncatedTextCell'
import {
  CampaignsCell,
  ContactTagCellV2,
  DistributorsCell,
  GPOsCell,
  StagePill,
  TaglistCell,
} from '../../FbUI/StagePill'
import ExpandedDealsRow from './ExpandedDealsRow'
import useApiService from 'services/api'
import { useUsers } from 'services/users'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { Badge } from '../../NewCompanyPage/Sidebar/shared/Badge'
import { DEAL_COLUMNS } from 'components/DataExpansionComponents/Deals/dealsColumnInfo'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { DealReport } from 'models/deals_reports'
import { Link } from 'react-router-dom'
import { useCompanyV2Url } from 'utils/useCompanyUrl'

interface UseDealsTableColumnsProps {
  sorting: SortingState
  showCompanyColumn?: boolean
  enabledReportDeals?: boolean
}

/**
 * Renders an account owner as a 24×24 gray avatar with initials + full name.
 * Matches the owner column treatment on the Campaigns table.
 */
function OwnerAvatarCell({ account_owner_id }: { account_owner_id?: number }) {
  const { data } = useUsers()
  if (!account_owner_id) return <>—</>
  const owner = data?.find((u) => u.id === account_owner_id)
  if (!owner) return <>—</>
  const name = `${owner.first_name ?? ''} ${owner.last_name ?? ''}`.trim()
  const initials =
    `${owner.first_name?.[0] ?? ''}${owner.last_name?.[0] ?? ''}`.toUpperCase()
  return (
    <div className="flex items-center gap-2.5">
      <InitialsAvatar initials={initials} />
      <span className="truncate">{name}</span>
    </div>
  )
}

export function useDealsTableColumnsV2({
  sorting,
  showCompanyColumn,
}: UseDealsTableColumnsProps): ColumnDef<DealReport>[] {
  const getV2Url = useCompanyV2Url()
  const api = useApiService()
  const { preferences } = usePreferences()
  const { data: categories } = api.useGetActiveCompanyCategoriesAndTypes()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const dealReportColumnHelper = createColumnHelper<DealReport>()
  const reportsColumnHelper = createTableColumnHelper({
    columns: DEAL_COLUMNS,
    tooltips,
  })
  const { enableEmailIntegration, dealDetailPage } = useFeatureFlagService()
  const dealsReportsColumns = useMemo(() => {
    /** Company column definition shared by both column layouts. */
    function companyColumn(
      meta: Record<string, unknown>,
      { showHeader = false }: { showHeader?: boolean } = {}
    ): ColumnDef<DealReport, any> {
      return dealReportColumnHelper.accessor('cc_id', {
        ...reportsColumnHelper.retrieveColumn('company', { meta }),
        id: 'cc_company_name',
        header: () => (showHeader ? 'Company' : null),
        cell: (info) => {
          const row = info.row.original
          return (
            <TruncatedLinkCell
              text={row.cc_company_name || ''}
              to={`${getV2Url({
                company_type: row.cc_company_type,
                chain_id: row.cc_chain_id,
                university_id: row.cc_university_id,
                k12district_id: row.cc_k12district_id,
                lodging_id: row.cc_lodging_id,
                id: row.cc_id,
              })}`}
            />
          )
        },
        size: 350,
      })
    }

    /** Deal ID column used in legacy layout. */
    function identifierColumn(): ColumnDef<DealReport, any> {
      return dealReportColumnHelper.accessor('identifier', {
        ...reportsColumnHelper.retrieveColumn('identifier', {
          meta: { isEditable: true },
        }),
        header: () => 'Deal ID',
        size: 120,
        cell: (info) => {
          const value = info.getValue()
          return <TruncatedTextCell text={value ? `#${value}` : '-'} />
        },
      })
    }

    /** Product column shared by both column layouts. */
    function productColumn(
      meta: Record<string, unknown>
    ): ColumnDef<DealReport, any> {
      return dealReportColumnHelper.accessor('product_id', {
        ...reportsColumnHelper.retrieveColumn('product', { meta }),
        header: () => (showCompanyColumn ? 'Product' : ''),
        size: 250,
        cell: (info) => {
          const row = info.row.original
          return <TruncatedTextCell text={row.product_name || ''} />
        },
      })
    }

    /** New combined deal column: #ID on top, deal name as link below. Frozen first column. */
    function buildDealDetailColumns(): ColumnDef<DealReport, any>[] {
      const cols: ColumnDef<DealReport, any>[] = []

      // Deal column is always first and frozen
      cols.push({
        ...reportsColumnHelper.retrieveColumn('deal', {
          meta: {
            isSticky: true,
            isFirstColumn: true,
            // Renders "#id" over the deal-name link — keep two lines.
            multiLine: true,
          },
        }),
        header: () => null,
        size: 300,
        cell: (info: CellContext<DealReport, unknown>) => {
          const row = info.row.original
          const dealName =
            row.deal_display_name || row.cc_company_name || 'Untitled Deal'
          return (
            <div className="flex flex-col gap-0.5 py-1 min-w-0">
              {/* text-xs ≈ 11px at the 14px root (text-xxs was 10px) */}
              <span className="text-xs text-fg-secondary">
                {row.identifier ? `#${row.identifier}` : '-'}
              </span>
              <Link
                to={`/deals/${row.id}`}
                // !text-sm (≈12px at the 14px root) overrides the S.TD `& a`
                // rule that otherwise pins table links to 14px.
                className="truncate !text-sm font-semibold text-fg hover:text-brand"
              >
                {dealName}
              </Link>
            </div>
          )
        },
      })

      // Company column is second and hideable (not frozen, not required)
      if (showCompanyColumn) {
        cols.push(companyColumn({ isEditable: true }, { showHeader: true }))
      }

      cols.push(productColumn({ isEditable: showCompanyColumn }))

      return cols
    }

    /** Legacy columns: separate company, deal ID, product, deal name. */
    function buildLegacyColumns(): ColumnDef<DealReport, any>[] {
      const cols: ColumnDef<DealReport, any>[] = []

      if (showCompanyColumn) {
        cols.push(
          companyColumn({ isEditable: false, isFirstColumn: true }),
          identifierColumn()
        )
      } else {
        cols.push(identifierColumn())
      }

      cols.push(
        productColumn({
          isEditable: showCompanyColumn,
          isFirstColumn: !showCompanyColumn,
        }),
        dealReportColumnHelper.accessor('deal_display_name', {
          ...reportsColumnHelper.retrieveColumn('deal_name'),
          header: () => 'Deal Name',
          size: 250,
          cell: (info) => {
            const row = info.row.original
            const dealName = row.deal_display_name || row.cc_company_name || '-'
            return <TruncatedTextCell text={dealName} />
          },
        })
      )

      return cols
    }

    const leadingColumns: ColumnDef<DealReport, any>[] = dealDetailPage
      ? buildDealDetailColumns()
      : buildLegacyColumns()

    let currentColumns: ColumnDef<DealReport, any>[] = [
      ...(dealDetailPage
        ? []
        : [
            {
              id: 'expanded-row',
              header: '',
              cell: (info: CellContext<DealReport, unknown>) => {
                const row = info.row.original
                return (
                  <ExpandedDealsRow
                    dealId={row.id}
                    productName={row.product_name}
                  />
                )
              },
            } as ColumnDef<DealReport, any>,
          ]),
      ...leadingColumns,
      dealReportColumnHelper.accessor('product_variant_name', {
        ...reportsColumnHelper.retrieveColumn('product_variant'),
        id: 'product_variant_name',
        header: () => 'Product Variant',
        size: 200,
        cell: (info) => {
          const row = info.row.original
          return row.product_variant_name || '-'
        },
      }),
      // "Company Size" (cc_doors_cnt) intentionally omitted here: it duplicated
      // the "Deal Size" column (doors_count). For entire-company deals the two
      // values are identical, so the column read as a duplicate (FIR-7468).
      // Deal Size is the more precise, deal-scoped field; company chain size
      // remains available on the Contacts table and as the company_size filter.
      dealReportColumnHelper.accessor('cc_category_id', {
        ...reportsColumnHelper.retrieveColumn('category'),
        id: 'cc_category_name',
        cell: (info) => {
          return (
            categories?.find(
              (a) => a.id === (info.getValue() as unknown as number)
            )?.name ?? '-'
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_sub_category_id', {
        ...reportsColumnHelper.retrieveColumn('sub_category'),
        id: 'cc_sub_category_name',
        cell: (info) => {
          let subCategoryName = '-'
          for (const category of categories ?? []) {
            const subCategory = category.sub_categories.find(
              (subCat) => subCat.id === (info.getValue() as unknown as number)
            )
            if (subCategory) {
              subCategoryName = subCategory.name
              break
            }
          }
          return subCategoryName
        },
      }),
      dealReportColumnHelper.accessor('sales_stage_id', {
        ...reportsColumnHelper.retrieveColumn('sales_stage'),
        id: 'sales_stage_name',
        size: 250,
        cell: (info) => {
          if (!info.row?.original?.sales_stage_name) return <>-</>
          return <StagePill stageId={info.row.original?.sales_stage_id} />
        },
      }),
      dealReportColumnHelper.accessor('last_datetime_stage_changed', {
        ...reportsColumnHelper.retrieveColumn('last_datetime_stage_changed'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().diff(dayjs(value), 'day')} days`
        },
      }),
      dealReportColumnHelper.accessor('expected_days_to_close', {
        ...reportsColumnHelper.retrieveColumn('expected_days_to_close'),
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().add(value, 'day').format('MMM DD, YYYY')}`
        },
      }),
      dealReportColumnHelper.accessor('close_date_override', {
        ...reportsColumnHelper.retrieveColumn('close_date_override'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return dayjs(value).format('MMM DD, YYYY')
        },
      }),
      dealReportColumnHelper.accessor('account_owner_id', {
        ...reportsColumnHelper.retrieveColumn('account_owner'),
        id: 'account_owner_name',
        size: 220,
        cell: (info) => {
          return <OwnerAvatarCell account_owner_id={info.getValue()} />
        },
      }),
      dealReportColumnHelper.accessor('days_since_reply', {
        ...reportsColumnHelper.retrieveColumn('days_since_reply'),
        cell: (info) => {
          return info.getValue() ?? '-'
        },
      }),
      dealReportColumnHelper.accessor('days_since_sent', {
        ...reportsColumnHelper.retrieveColumn('days_since_sent'),
        cell: (info) => {
          return info.getValue() ?? '-'
        },
      }),
      dealReportColumnHelper.accessor('origin_campaign_id', {
        ...reportsColumnHelper.retrieveColumn('origin_campaign'),
        size: 260,
        cell: (info) => {
          const campIds = info.row.original.origin_campaign_id
            ? [info.row.original.origin_campaign_id]
            : []
          return <CampaignsCell campaignsIds={campIds} />
        },
      }),
      dealReportColumnHelper.accessor('annual_revenue', {
        ...reportsColumnHelper.retrieveColumn('annual_revenue'),
        size: 200,
        cell: (info) => {
          const value = info.getValue()
          const row = info.row.original
          const isNonRecurring = row.is_not_recurring

          if (!value) return <>-</>

          return (
            <div className="flex items-center gap-1.5">
              <span>{formatUsd(value)}</span>
              {isNonRecurring && (
                <Badge variant="gray" size="sm">
                  fixed
                </Badge>
              )}
            </div>
          )
        },
      }),
      dealReportColumnHelper.accessor('monthly_revenue_predicted', {
        ...reportsColumnHelper.retrieveColumn('monthly_revenue_predicted'),
        cell: (info) => {
          const row = info.row.original
          // Non-recurring products don't have monthly values
          if (row.is_not_recurring) return <>N/A</>
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      dealReportColumnHelper.accessor('monthly_revenue_override', {
        ...reportsColumnHelper.retrieveColumn('monthly_revenue_override'),
        size: 250,
        cell: (info) => {
          const value = info.getValue()
          const row = info.row.original
          const isNonRecurring = row.is_not_recurring

          if (!value) return <>-</>

          return (
            <div className="flex items-center gap-1.5">
              <span>{formatUsdSmartDecimal(value)}</span>
              <Badge variant="gray" size="sm">
                {isNonRecurring ? 'fixed' : 'monthly'}
              </Badge>
            </div>
          )
        },
      }),
      dealReportColumnHelper.accessor('annual_volume', {
        ...reportsColumnHelper.retrieveColumn('annual_volume'),
        size: 200,
        cell: (info) => {
          const value = info.getValue()
          const row = info.row.original
          const isNonRecurring = row.is_not_recurring

          if (!value) return <>-</>

          return (
            <div className="flex items-center gap-1.5">
              <span>{formatTwoDecimal(value ?? 0)}</span>
              {isNonRecurring && (
                <Badge variant="gray" size="sm">
                  fixed
                </Badge>
              )}
            </div>
          )
        },
      }),
      dealReportColumnHelper.accessor('monthly_volume_predicted', {
        ...reportsColumnHelper.retrieveColumn('monthly_volume_predicted'),
        cell: (info) => {
          const row = info.row.original
          // Non-recurring products don't have monthly values
          if (row.is_not_recurring) return <>N/A</>
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('annual_brand_opp', {
        ...reportsColumnHelper.retrieveColumn('annual_brand_opp'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatUsd(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('annual_total', {
        ...reportsColumnHelper.retrieveColumn('annual_total'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatUsd(value ?? 0)}`
        },
      }),
      dealReportColumnHelper.accessor('monthly_volume_override', {
        ...reportsColumnHelper.retrieveColumn('monthly_volume_override'),
        size: 175,
        cell: (info) => {
          const value = info.getValue()
          const row = info.row.original
          const isNonRecurring = row.is_not_recurring

          if (!value) return <>-</>

          return (
            <div className="flex items-center gap-1.5">
              <span>{formatTwoDecimal(value ?? 0)}</span>
              <Badge variant="gray" size="sm">
                {isNonRecurring ? 'fixed' : 'monthly'}
              </Badge>
            </div>
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_taglist', {
        ...reportsColumnHelper.retrieveColumn('taglist'),
        size: 260,
        cell: (info) => {
          return (
            <TaglistCell
              taglistIds={info.row.original?.cc_taglist?.map((tag) => tag.id!)}
              tagType="products"
              asc={
                sorting?.length && sorting[0].id === 'taglist'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('deal_taglist', {
        ...reportsColumnHelper.retrieveColumn('deal_taglist'),
        size: 260,
        cell: (info) => {
          return (
            <TaglistCell
              taglistIds={info.row.original?.deal_taglist?.map(
                (tag) => tag.id!
              )}
              tagType="deals"
              asc={
                sorting?.length && sorting[0].id === 'deal_taglist'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('contact_tags', {
        ...reportsColumnHelper.retrieveColumn('contact_tags'),
        size: 260,
        cell: (info) => {
          return (
            <ContactTagCellV2
              contact_tags={info.row.original.contact_tags?.map(
                (tag) => tag.id!
              )}
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_distributors', {
        ...reportsColumnHelper.retrieveColumn('distributors'),
        id: 'cc_distributors',
        size: 260,
        cell: (info) => {
          return (
            <DistributorsCell
              distributorsIds={info.row.original?.cc_distributors?.map(
                (dist) => dist.id!
              )}
              asc={
                sorting?.length && sorting[0].id === 'cc_distributors'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_gpos', {
        ...reportsColumnHelper.retrieveColumn('gpos'),
        id: 'cc_gpos',
        size: 260,
        cell: (info) => {
          return (
            <GPOsCell
              gposIds={info.row.original?.cc_gpos?.map((gpo) => gpo.id!)}
              asc={
                sorting?.length && sorting[0].id === 'cc_gpos'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      dealReportColumnHelper.accessor('cc_note_count', {
        ...reportsColumnHelper.retrieveColumn('note_count'),
        id: 'cc_note_count',
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('cc_contact_count', {
        ...reportsColumnHelper.retrieveColumn('contact_count'),
        id: 'cc_contact_count',
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('days_since_last_modified', {
        ...reportsColumnHelper.retrieveColumn('days_since_last_modified'),
        size: 180,
        cell: (info) => {
          return info.getValue()
        },
      }),
      dealReportColumnHelper.accessor('days_since_deal_last_modified', {
        ...reportsColumnHelper.retrieveColumn('days_since_deal_last_modified'),
        cell: (info) => {
          return info.getValue()
        },
      }),
      dealReportColumnHelper.accessor('estimated_meals_per_day', {
        ...reportsColumnHelper.retrieveColumn('estimated_meals_per_day'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('operational_schools', {
        ...reportsColumnHelper.retrieveColumn('operational_schools'),
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      dealReportColumnHelper.accessor('modified', {
        ...reportsColumnHelper.retrieveColumn('modified'),
        size: 150,
        cell: (info) => {
          return dayjs(info.getValue()).format('MM/DD/YYYY')
        },
      }),
      dealReportColumnHelper.accessor('created', {
        ...reportsColumnHelper.retrieveColumn('created'),
        size: 150,
        cell: (info) => dayjs(info.getValue()).format('MM/DD/YYYY'),
      }),
      dealReportColumnHelper.accessor('doors_count', {
        ...reportsColumnHelper.retrieveColumn('doors_count'),
        size: 150,
        cell: (info) => info.getValue() || '-',
      }),
    ]

    if (!enableEmailIntegration) {
      currentColumns = currentColumns.filter(
        (column) =>
          column.id !== 'days_since_reply' && column.id !== 'days_since_sent'
      )
    }

    return currentColumns
  }, [categories, tooltips, dealDetailPage, showCompanyColumn])

  return dealsReportsColumns
}
